import React, { useCallback, useMemo } from 'react'
import { toast } from 'react-toastify'
import { useHistory } from 'react-router-dom'
import { useApi } from '~/store/store'
import { DoubleCheckIcon } from '~/assets/icons'
import { mutualTheme } from '~/utils/theme'
import { makeStyles } from '@material-ui/styles'
import { ServiceResponse } from '@mutual-libs/shared-types'
import { Button, CircularProgress } from '@material-ui/core'
import { useAuthAccount } from '~/store/auth/useAuthAccount'
import { FetchRequestDocumentsOutput, checkForMissingRequiredDocuments } from '~/utils/documents'
import { DocumentsGroup } from '@mutual-services/account-documents-types'
import { AccountAnalysisStatus } from '@mutual-services/account-types'
import { useModal } from '~/hooks'
import { DocumentationSentFeedback } from '~/components'
import { TrackEventType } from '@mutual-services/marketing-types'

const useStyles = makeStyles({
  button: {
    height: 56,
    fontWeight: 700,
    textTransform: 'none'
  }
})

export type AdvanceStepButtonProps = {
  docData: FetchRequestDocumentsOutput[],
  documentsList: ServiceResponse<DocumentsGroup[]>
}

export const AdvanceStepButton: React.FC<AdvanceStepButtonProps> = (props) => {
  const { docData, documentsList } = props
  const { _id: accountId, email } = useAuthAccount()
  const [isOpen, toggleModal] = useModal()
  const [updatePJAnalysisStatus, updatePJAnalysisStatusState] = useApi('updatePJAnalysisStatus')
  const [sendDocumentsLead, sendDocumentsLeadState] = useApi('sendDocumentsLead')

  const isLoading = useMemo(() => updatePJAnalysisStatusState.loading || sendDocumentsLeadState.loading, [updatePJAnalysisStatusState.loading, sendDocumentsLeadState.loading])

  const history = useHistory()
  const classes = useStyles()

  const isMissingRequiredDocuments = checkForMissingRequiredDocuments(docData, documentsList)
  const isDisabled = useMemo(() => (isLoading || isMissingRequiredDocuments), [isLoading, isMissingRequiredDocuments])

  const onClick = useCallback(async () => {
    const result = await updatePJAnalysisStatus({ accountId, pjAnalysisStatus: { status: AccountAnalysisStatus.WAITING_FOR_ANALYSIS, denialReason: null } })
    if (result.ok) {
      await sendDocumentsLead({ accountId, email, eventType: TrackEventType.pjDocSent })
      toggleModal()
      return
    }

    toast.error('Erro ao concluir etapa de Cadastro de Documentação. Tente novamente em alguns instantes.', {
      autoClose: 3000,
      closeButton: false,
      position: toast.POSITION.TOP_RIGHT
    })
  }, [toast, history, toggleModal, accountId, email, sendDocumentsLead])

  return (
    <>
      <Button
        type='button'
        size='large'
        color='secondary'
        onClick={onClick}
        variant='contained'
        disabled={isDisabled}
        className={classes.button}
        startIcon={isLoading
          ? <CircularProgress color='inherit' size={17} />
          : <DoubleCheckIcon htmlColor={isDisabled ? mutualTheme.palette.textGray2.main : mutualTheme.palette.common.white} />
        }
      >
        Concluir
      </Button>
      <DocumentationSentFeedback isVisible={isOpen} onClose={toggleModal} />
    </>
  )
}
